import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import RoleCard from "../../components/roles/role_card";
import { motion, AnimatePresence } from "framer-motion";
import SaveNotification from "../../components/notification/save_notification";
import MatchRequestNotification from "../../components/notifications/MatchRequestNotification";
import { useMatchRequests } from '../../hooks/useMatchRequests';
import StartupVisibilityModal from '../../components/modals/StartupVisibilityModal';
import WelcomeModal from '../../components/modals/WelcomeModal';
import HelpPanel from '../../components/HelpPanel';
import { useAuthRedirect } from '../../hooks/useAuthRedirect';



// === PERSONALIZED FILTERING UTILITIES ===

/**
 * Parse salary string and return min/max range (e.g., "£45k" -> {min: 45, max: 45}, "£30-60k" -> {min: 30, max: 60})
 */
/**
 * FIXED: Parse salary string and return min/max range in thousands
 * Example: "£40,000 - 45,000" -> {min: 40, max: 45}
 */
const parseSalaryRange = (salaryStr) => {
  if (!salaryStr) return null;
  
  // Clean the string - remove £, spaces, commas, convert to lowercase
  const clean = salaryStr.toString().toLowerCase().replace(/[£,\s]/g, '');
  
  console.log('🔍 Salary parsing debug - Original:', salaryStr);
  console.log('🔍 Salary parsing debug - After cleaning:', clean);
  
  // Handle "up to X" format first
  let match = clean.match(/upto(\d+)k?/);
  if (match) {
    const max = parseInt(match[1]);
    const finalMax = max > 1000 ? Math.round(max / 1000) : max;
    console.log('Found "up to" format:', `0k-${finalMax}k`);
    return { min: 0, max: finalMax };
  }
  
  // FIXED: Handle full numbers FIRST (before the generic k range pattern)
  // This handles "40000-45000" or "40,000-50,000" (after comma removal)
  match = clean.match(/(\d{4,6})[-–—](\d{4,6})/);
  if (match) {
    const min = Math.round(parseInt(match[1]) / 1000);
    const max = Math.round(parseInt(match[2]) / 1000);
    console.log('Found full number range:', `${min}k-${max}k`);
    return { min, max };
  }
  
  // Handle "X to Y" format with full numbers
  match = clean.match(/(\d{4,6})to(\d{4,6})/);
  if (match) {
    const min = Math.round(parseInt(match[1]) / 1000);
    const max = Math.round(parseInt(match[2]) / 1000);
    console.log('Found full number "to" format:', `${min}k-${max}k`);
    return { min, max };
  }
  
  // Handle ranges with 'k' notation like "30k-60k", "£30k-£60k"
  match = clean.match(/(\d+)k[-–—](\d+)k/);
  if (match) {
    const min = parseInt(match[1]);
    const max = parseInt(match[2]);
    console.log('Found k-to-k range format:', `${min}k-${max}k`);
    return { min, max };
  }
  
  // Handle mixed formats like "30-60k" or "30k-60"
  match = clean.match(/(\d+)k?[-–—](\d+)k?/);
  if (match) {
    let min = parseInt(match[1]);
    let max = parseInt(match[2]);
    
    // If numbers are large (>1000), convert to thousands
    if (min > 1000) min = Math.round(min / 1000);
    if (max > 1000) max = Math.round(max / 1000);
    
    console.log('Found mixed range format:', `${min}k-${max}k`);
    return { min, max };
  }
  
  // Handle "X to Y" format with k notation
  match = clean.match(/(\d+)k?to(\d+)k?/);
  if (match) {
    let min = parseInt(match[1]);
    let max = parseInt(match[2]);
    
    // If numbers are large (>1000), convert to thousands
    if (min > 1000) min = Math.round(min / 1000);
    if (max > 1000) max = Math.round(max / 1000);
    
    console.log('Found "to" format:', `${min}k-${max}k`);
    return { min, max };
  }
  
  // Handle single values with "k" - "45k", "£45k"
  match = clean.match(/^(\d+)k$/);
  if (match) {
    const value = parseInt(match[1]);
    console.log('Found single k value:', `${value}k`);
    return { min: value, max: value };
  }
  
  // Handle full numbers like "45000" (convert to thousands)
  match = clean.match(/^(\d{4,6})$/);
  if (match) {
    const value = Math.round(parseInt(match[1]) / 1000);
    console.log('Found single full number:', `${value}k`);
    return { min: value, max: value };
  }
  
  // Handle simple numbers (assume they're already in thousands if reasonable)
  match = clean.match(/^(\d+)$/);
  if (match) {
    const value = parseInt(match[1]);
    // Only treat as salary if it's a reasonable range (10-500k)
    if (value >= 10 && value <= 500) {
      console.log('Found simple number:', `${value}k`);
      return { min: value, max: value };
    }
  }
  
  console.log('❌ No salary pattern matched for:', clean);
  return null;
};

/**
 * IMPROVED: Parse candidate salary to number with better validation
 */
const parseCandidateSalary = (salaryStr) => {
  if (!salaryStr) return null;
  
  const clean = salaryStr.toString().toLowerCase().replace(/[£,\s]/g, '');
  
  // Look for "k" format first
  let match = clean.match(/(\d+)k/);
  if (match) {
    return parseInt(match[1]);
  }
  
  // Look for full numbers and convert to thousands
  match = clean.match(/(\d{4,6})/);
  if (match) {
    return Math.round(parseInt(match[1]) / 1000);
  }
  
  // Look for simple numbers (assume thousands if reasonable)
  match = clean.match(/(\d+)/);
  if (match) {
    const num = parseInt(match[1]);
    // If it's a reasonable salary range, assume it's already in k
    if (num >= 10 && num <= 500) {
      return num;
    }
  }
  
  return null;
};

/**
 * IMPROVED: Check if role salary range overlaps with candidate preferences
 * Now with better debugging and validation
 */
const checkSalaryMatch = (roleCompensation, candidateMin, candidateIdeal, salaryBuffer = 5) => {
  console.log('\n🎯 === SALARY MATCHING DEBUG ===');
  console.log('Input values:', { roleCompensation, candidateMin, candidateIdeal, salaryBuffer });
  
  if (!roleCompensation || !candidateMin || !candidateIdeal) {
    console.log('❌ Missing salary data');
    return false;
  }
  
  // Parse role salary range
  const roleRange = parseSalaryRange(roleCompensation);
  if (!roleRange) {
    console.log('❌ Could not parse role salary:', roleCompensation);
    return false;
  }
  
  // Validate role range
  if (roleRange.min > roleRange.max) {
    console.log('❌ Invalid role range (min > max):', roleRange);
    return false;
  }
  
  // Parse candidate salaries
  const candidateMinSalary = parseCandidateSalary(candidateMin);
  const candidateIdealSalary = parseCandidateSalary(candidateIdeal);
  
  if (!candidateMinSalary || !candidateIdealSalary) {
    console.log('❌ Could not parse candidate salaries:', { candidateMin, candidateIdeal });
    return false;
  }
  
  // Validate candidate range
  if (candidateMinSalary > candidateIdealSalary) {
    console.log('⚠️ Warning: Candidate min > ideal, swapping values');
    // Swap them if they're backwards
    const temp = candidateMinSalary;
    candidateMinSalary = candidateIdealSalary;
    candidateIdealSalary = temp;
  }
  
  console.log('✅ Parsed values:');
  console.log(`   Role Range: ${roleRange.min}k - ${roleRange.max}k`);
  console.log(`   Candidate Range: ${candidateMinSalary}k - ${candidateIdealSalary}k`);
  console.log(`   Buffer: ±${salaryBuffer}k`);
  
  // Apply buffer to candidate range (candidate is more flexible)
  const candidateMinWithBuffer = Math.max(0, candidateMinSalary - salaryBuffer);
  const candidateMaxWithBuffer = candidateIdealSalary + salaryBuffer;
  
  console.log(`   Candidate Range with Buffer: ${candidateMinWithBuffer}k - ${candidateMaxWithBuffer}k`);
  
  // Check for overlap between ranges
  // Two ranges overlap if: max1 >= min2 AND min1 <= max2
  const hasOverlap = (roleRange.max >= candidateMinWithBuffer) && (roleRange.min <= candidateMaxWithBuffer);
  
  console.log('🔍 Overlap Calculation:');
  console.log(`   roleRange.max (${roleRange.max}) >= candidateMinWithBuffer (${candidateMinWithBuffer}): ${roleRange.max >= candidateMinWithBuffer}`);
  console.log(`   roleRange.min (${roleRange.min}) <= candidateMaxWithBuffer (${candidateMaxWithBuffer}): ${roleRange.min <= candidateMaxWithBuffer}`);
  console.log(`   Final Result: ${hasOverlap ? '✅ MATCH' : '❌ NO MATCH'}`);
  
  return hasOverlap;
};

/**
 * Check if role working style matches candidate preferences
 */
const checkWorkingStyleMatch = (roleWorkingStyle, candidateWorkingStyles, relaxed = false) => {
  if (!roleWorkingStyle || !candidateWorkingStyles || candidateWorkingStyles.length === 0) return false;
  
  // Normalize role working style (from "Working style category" field)
  const normalizeRoleStyle = (style) => {
    if (!style) return '';
    const normalized = style.toLowerCase().trim();
    
    if (normalized === 'in person') return 'in-person';
    if (normalized === 'hybrid') return 'hybrid';
    if (normalized === 'remote') return 'remote';
    if (normalized === 'tbc') return 'tbc'; // To be confirmed
    
    return normalized;
  };
  
  const normalizedRoleStyle = normalizeRoleStyle(roleWorkingStyle);
  
  // Define what role styles each candidate preference accepts
  const candidateToRoleMatching = {
    // Fully remote candidates
    "Fully remote": {
      strict: ["remote"],
      relaxed: ["remote", "hybrid"] // In relaxed mode, might consider hybrid
    },
    
    // Hybrid preferences with specific days
    "Hybrid - 2/3 days in the office": {
      strict: ["hybrid"],
      relaxed: ["hybrid", "in-person"] // Might consider full office
    },
    
    "Hybrid (1/2 days in the office) or Fully remote": {
      strict: ["hybrid", "remote"],
      relaxed: ["hybrid", "remote"]
    },
    
    "Hybrid (1-4 days in office)": {
      strict: ["hybrid"],
      relaxed: ["hybrid", "in-person", "remote"] // Very flexible range
    },
    
    "Hybrid - 3-4 days in the office; 1-2 remote": {
      strict: ["hybrid"],
      relaxed: ["hybrid", "in-person"] // Leans more toward office
    },
    
    "Hybrid (2/3 days) or fully in office": {
      strict: ["hybrid", "in-person"],
      relaxed: ["hybrid", "in-person"]
    },
    
    // Fully in-person candidates
    "Fully in-person": {
      strict: ["in-person"],
      relaxed: ["in-person", "hybrid"] // Might consider hybrid
    },
    
    "5 days a week in office": {
      strict: ["in-person"],
      relaxed: ["in-person", "hybrid"] // Might consider hybrid
    },
    
    // Flexible/mixed preferences
    "Flexible": {
      strict: ["hybrid", "in-person", "remote"], // Accepts anything
      relaxed: ["hybrid", "in-person", "remote", "tbc"]
    },
    
    "Remote or Hybrid": {
      strict: ["remote", "hybrid"],
      relaxed: ["remote", "hybrid"]
    },
    
    "Hybrid or fully in-person": {
      strict: ["hybrid", "in-person"],
      relaxed: ["hybrid", "in-person"]
    },
    
    // Minimal office requirements
    "Periodic office visits based on project needs": {
      strict: ["remote", "hybrid"],
      relaxed: ["remote", "hybrid"]
    },
    
    "In office once every 2 weeks": {
      strict: ["remote", "hybrid"],
      relaxed: ["remote", "hybrid"]
    },
    
    "Remote / Hybrid - on and off months in and out of office depending on travel plans": {
      strict: ["remote", "hybrid"],
      relaxed: ["remote", "hybrid"]
    },
    
    "1-2 days a month in the office": {
      strict: ["remote", "hybrid"],
      relaxed: ["remote", "hybrid"]
    }
  };
  
  // Check if any candidate preference matches the role
  for (const candidateStyle of candidateWorkingStyles) {
    const matching = candidateToRoleMatching[candidateStyle];
    
    if (matching) {
      const acceptableRoleStyles = relaxed ? matching.relaxed : matching.strict;
      
      if (acceptableRoleStyles.includes(normalizedRoleStyle)) {
      return true;
    }
      
      // Special case: if role is "TBC", it's a potential match in relaxed mode
      if (relaxed && normalizedRoleStyle === 'tbc') {
        return true;
  }
    }
  }
  
  return false;
};

/**
 * Debug function to analyze experience matching (only used in debug mode)
 */
const debugExperienceMatch = (roleTitle, candidateExperiences) => {
  if (!roleTitle || !candidateExperiences || candidateExperiences.length === 0) {
    return { match: false, reasons: ['No data available'] };
  }

  const normalizeText = (text) => {
    if (!text) return '';
    return text.toLowerCase().trim();
  };

  const normalizedRoleTitle = normalizeText(roleTitle);
  const reasons = [];
  let matchFound = false;

  for (const experience of candidateExperiences) {
    if (!experience) continue;
    
    const normalizedExperience = normalizeText(experience);
    reasons.push(`\n--- Analyzing: "${roleTitle}" vs "${experience}" ---`);
    
    // Check each matching strategy
    if (normalizedRoleTitle === normalizedExperience) {
      reasons.push(`✅ EXACT MATCH`);
      matchFound = true;
      break;
    }
    
    if (normalizedRoleTitle.includes(normalizedExperience) || normalizedExperience.includes(normalizedRoleTitle)) {
      reasons.push(`✅ SUBSTRING MATCH`);
      matchFound = true;
      break;
    }
    
    // Category matching
    const roleCategories = {
      'software_engineering': ['software engineer', 'developer', 'programmer', 'full stack', 'frontend', 'backend'],
      'data_analytics': ['data analyst', 'data scientist', 'data engineer', 'analytics', 'business analyst'],
      'product_design': ['product manager', 'product owner', 'ux designer', 'ui designer'],
      'sales_business': ['sales', 'account executive', 'business development', 'bdr', 'sdr'],
      'marketing': ['marketing', 'digital marketing', 'growth', 'seo', 'content'],
      'operations_customer': ['operations', 'customer success', 'customer support', 'project manager'],
      'finance_legal': ['financial', 'accountant', 'legal', 'compliance'],
      'hr_recruiting': ['hr', 'human resources', 'recruiter', 'talent acquisition']
    };
    
    for (const [category, terms] of Object.entries(roleCategories)) {
      const roleInCategory = terms.some(term => normalizedRoleTitle.includes(term));
      const experienceInCategory = terms.some(term => normalizedExperience.includes(term));
      
      if (roleInCategory && experienceInCategory) {
        reasons.push(`✅ CATEGORY MATCH (${category})`);
        matchFound = true;
        break;
      }
    }
    
    if (matchFound) break;
    
    // Word overlap
    const experienceWords = normalizedExperience.split(' ').filter(w => w.length > 2);
    const roleWords = normalizedRoleTitle.split(' ').filter(w => w.length > 2);
    
    if (experienceWords.length > 0 && roleWords.length > 0) {
      const commonWords = experienceWords.filter(word => roleWords.includes(word));
      const overlapRatio = commonWords.length / Math.min(experienceWords.length, roleWords.length);
      
      reasons.push(`Word overlap: ${commonWords.length}/${Math.min(experienceWords.length, roleWords.length)} = ${Math.round(overlapRatio * 100)}%`);
      
      if (overlapRatio >= 0.6) {
        reasons.push(`✅ WORD OVERLAP MATCH (${Math.round(overlapRatio * 100)}%)`);
        matchFound = true;
        break;
      }
    }
    
    reasons.push(`❌ No match found`);
  }

  return { match: matchFound, reasons };
};

/**
 * Check if candidate's experience matches the role title (holistic semantic matching)
 */
const checkExperienceMatch = (roleTitle, candidateExperiences) => {
  if (!roleTitle || !candidateExperiences || candidateExperiences.length === 0) {
    return false;
  }

  const normalizeText = (text) => {
    if (!text) return '';
    return text.toLowerCase().trim();
  };

  const normalizedRoleTitle = normalizeText(roleTitle);
  
  // Define role categories and their related terms for semantic matching
  const roleCategories = {
    // Software Engineering
    'software_engineering': [
      'software engineer', 'software developer', 'developer', 'programmer', 'coder',
      'full stack', 'frontend', 'backend', 'fullstack', 'web developer', 'application developer',
      'systems engineer', 'devops engineer', 'site reliability engineer', 'sre',
      'mobile developer', 'ios developer', 'android developer', 'react developer', 'vue developer',
      'angular developer', 'node.js developer', 'python developer', 'java developer', 'c# developer',
      'golang developer', 'rust developer', 'php developer', 'ruby developer', 'scala developer'
    ],
    
    // Data & Analytics
    'data_analytics': [
      'data analyst', 'data scientist', 'data engineer', 'analytics engineer', 'business analyst',
      'data analytics', 'data science', 'machine learning engineer', 'ml engineer', 'ai engineer',
      'statistical analyst', 'quantitative analyst', 'bi analyst', 'business intelligence analyst',
      'research analyst', 'market analyst', 'financial analyst', 'product analyst'
    ],
    
    // Product & Design
    'product_design': [
      'product manager', 'product management', 'product owner', 'scrum master', 'agile coach',
      'ux designer', 'ui designer', 'user experience designer', 'user interface designer',
      'product designer', 'interaction designer', 'visual designer', 'graphic designer',
      'designer', 'creative director', 'art director'
    ],
    
    // Sales & Business Development
    'sales_business': [
      'sales representative', 'sales rep', 'account executive', 'sales executive', 'business development',
      'business development representative', 'bdr', 'sales development representative', 'sdr',
      'account manager', 'sales manager', 'sales director', 'commercial director',
      'partnership manager', 'channel sales', 'inside sales', 'outside sales'
    ],
    
    // Marketing
    'marketing': [
      'marketing manager', 'marketing specialist', 'digital marketing', 'content marketing',
      'social media manager', 'growth marketing', 'growth hacker', 'seo specialist',
      'ppc specialist', 'email marketing', 'brand manager', 'communications manager',
      'pr manager', 'public relations', 'marketing coordinator', 'campaign manager'
    ],
    
    // Operations & Customer Success
    'operations_customer': [
      'operations manager', 'operations analyst', 'customer success', 'customer success manager',
      'customer support', 'customer service', 'account manager', 'client success',
      'operations coordinator', 'process manager', 'project manager', 'program manager'
    ],
    
    // Finance & Legal
    'finance_legal': [
      'financial analyst', 'accountant', 'bookkeeper', 'finance manager', 'controller',
      'cfo', 'chief financial officer', 'legal counsel', 'lawyer', 'attorney',
      'paralegal', 'compliance officer', 'risk manager', 'investment analyst'
    ],
    
    // HR & Recruiting
    'hr_recruiting': [
      'hr manager', 'human resources', 'recruiter', 'talent acquisition', 'hr specialist',
      'people operations', 'hr coordinator', 'talent manager', 'recruitment consultant',
      'hr business partner', 'hrbp', 'people manager'
    ]
  };

  // Helper function to calculate word similarity
  const calculateWordSimilarity = (word1, word2) => {
    if (word1 === word2) return 1;
    if (word1.includes(word2) || word2.includes(word1)) return 0.8;
    
    // Check for common abbreviations and variations
    const abbreviations = {
      'eng': 'engineer', 'engr': 'engineer', 'dev': 'developer', 'mgr': 'manager',
      'dir': 'director', 'exec': 'executive', 'rep': 'representative', 'spec': 'specialist',
      'coord': 'coordinator', 'analyst': 'analytics', 'analytics': 'analyst'
    };
    
    const normalized1 = abbreviations[word1] || word1;
    const normalized2 = abbreviations[word2] || word2;
    
    if (normalized1 === normalized2) return 0.9;
    if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) return 0.7;
    
    return 0;
  };

  // Helper function to calculate overall similarity score
  const calculateSimilarityScore = (text1, text2) => {
    const words1 = text1.split(' ').filter(word => word.length > 2);
    const words2 = text2.split(' ').filter(word => word.length > 2);
    
    if (words1.length === 0 || words2.length === 0) return 0;
    
    let totalSimilarity = 0;
    let comparisons = 0;
    
    // Compare each word in text1 with each word in text2
    for (const word1 of words1) {
      for (const word2 of words2) {
        const similarity = calculateWordSimilarity(word1, word2);
        totalSimilarity += similarity;
        comparisons++;
      }
    }
    
    return comparisons > 0 ? totalSimilarity / comparisons : 0;
  };

  // Check if any candidate experience matches the role title
  return candidateExperiences.some(experience => {
    if (!experience) return false;
    
    const normalizedExperience = normalizeText(experience);
    
    // 1. Direct exact match
    if (normalizedRoleTitle === normalizedExperience) {
      console.log(`🎯 Exact match: "${roleTitle}" ↔ "${experience}"`);
      return true;
    }
    
    // 2. Substring matching (one contains the other)
    if (normalizedRoleTitle.includes(normalizedExperience) || normalizedExperience.includes(normalizedRoleTitle)) {
      console.log(`🎯 Substring match: "${roleTitle}" ↔ "${experience}"`);
      return true;
    }
    
    // 3. Category-based semantic matching
    for (const [category, relatedTerms] of Object.entries(roleCategories)) {
      const roleInCategory = relatedTerms.some(term => 
        normalizedRoleTitle.includes(term) || term.includes(normalizedRoleTitle)
      );
      const experienceInCategory = relatedTerms.some(term => 
        normalizedExperience.includes(term) || term.includes(normalizedExperience)
      );
      
      if (roleInCategory && experienceInCategory) {
        console.log(`🎯 Category match (${category}): "${roleTitle}" ↔ "${experience}"`);
        return true;
      }
    }
    
    // 4. Word overlap analysis with improved threshold
    const experienceWords = normalizedExperience.split(' ').filter(word => word.length > 2);
    const roleWords = normalizedRoleTitle.split(' ').filter(word => word.length > 2);
    
    if (experienceWords.length > 0 && roleWords.length > 0) {
      const commonWords = experienceWords.filter(word => 
        roleWords.some(roleWord => calculateWordSimilarity(word, roleWord) > 0.7)
      );
      
      const overlapRatio = commonWords.length / Math.min(experienceWords.length, roleWords.length);
      if (overlapRatio >= 0.6) { // Lowered threshold for better matching
        console.log(`🎯 Word overlap match (${Math.round(overlapRatio * 100)}%): "${roleTitle}" ↔ "${experience}"`);
        return true;
      }
    }
    
    // 5. Overall similarity score
    const similarityScore = calculateSimilarityScore(normalizedRoleTitle, normalizedExperience);
    if (similarityScore >= 0.6) {
      console.log(`🎯 Similarity match (${Math.round(similarityScore * 100)}%): "${roleTitle}" ↔ "${experience}"`);
      return true;
    }
    
    // 6. Check for seniority level matches (Junior, Senior, Lead, etc.)
    const seniorityTerms = ['junior', 'senior', 'lead', 'principal', 'staff', 'head', 'chief', 'vp', 'director'];
    const roleHasSeniority = seniorityTerms.some(term => normalizedRoleTitle.includes(term));
    const experienceHasSeniority = seniorityTerms.some(term => normalizedExperience.includes(term));
    
    if (roleHasSeniority && experienceHasSeniority) {
      // If both have seniority terms, check if the core roles match
      const roleWithoutSeniority = normalizedRoleTitle.replace(/\b(junior|senior|lead|principal|staff|head|chief|vp|director)\b/g, '').trim();
      const experienceWithoutSeniority = normalizedExperience.replace(/\b(junior|senior|lead|principal|staff|head|chief|vp|director)\b/g, '').trim();
      
      if (roleWithoutSeniority && experienceWithoutSeniority) {
        const coreSimilarity = calculateSimilarityScore(roleWithoutSeniority, experienceWithoutSeniority);
        if (coreSimilarity >= 0.5) {
          console.log(`🎯 Seniority match: "${roleTitle}" ↔ "${experience}"`);
          return true;
        }
      }
    }
    
    return false;
  });
};



/**
 * Calculate personalized match score for a role (with experience bonus)
 */
const calculateMatchScore = async (role, candidateData, options = {}) => {
  const { salaryBuffer = 5, relaxedWorkingStyle = false } = options;
  
  let score = 0;
  let totalCriteria = 0;
  
  // Check individual matches (salary and working style are primary criteria)
  const hasSalaryData = candidateData.minimum_salary && candidateData.ideal_salary;
  const hasWorkingStyleData = candidateData.working_style && candidateData.working_style.length > 0;
  
  // Check experience match first to determine salary buffer
  const candidateExperiences = [
    candidateData.work_experience_1_role,
    candidateData.work_experience_2_role
  ].filter(Boolean);
  
  const experienceMatch = candidateExperiences.length > 0 ? 
    checkExperienceMatch(role["Role Title"], candidateExperiences) : false;
  
  // Use 10k buffer if there's an experience match, otherwise use default buffer
  const effectiveSalaryBuffer = experienceMatch ? 10 : salaryBuffer;
  
  const salaryMatch = hasSalaryData ? checkSalaryMatch(role["Compensation"], candidateData.minimum_salary, candidateData.ideal_salary, effectiveSalaryBuffer) : false;
  const workingStyleMatch = hasWorkingStyleData ? checkWorkingStyleMatch(role["Working style category"], candidateData.working_style, relaxedWorkingStyle) : false;
  
  // Calculate score based on available criteria (salary and working style are primary)
  if (hasSalaryData) {
    totalCriteria += 60;
    if (salaryMatch) score += 60;
  }
  
  if (hasWorkingStyleData) {
    totalCriteria += 40;
    if (workingStyleMatch) score += 40;
  }
  
  // Experience match gives a bonus (up to 20 points) but doesn't affect the base score
  let experienceBonus = 0;
  if (experienceMatch) {
    experienceBonus = 20; // Bonus for experience match
  }
  
  const baseScore = totalCriteria > 0 ? (score / totalCriteria) * 100 : 0;
  const finalScore = Math.min(100, baseScore + experienceBonus);
  
  return finalScore;
};

/**
 * Check if a role should be excluded due to high application count
 */
const getApplicationCount = (role) => {
  // Check various possible field names for application count
  const possibleFields = [
    "# Apps this month",
    "Apps this month", 
    "Applications this month",
    "# Applications this month",
    "App Count",
    "Application Count"
  ];
  
  for (const fieldName of possibleFields) {
    const appCount = role[fieldName];
    if (appCount !== undefined && appCount !== null) {
      const count = parseInt(appCount);
      if (!isNaN(count)) {
        return count;
      }
    }
  }
  
  return 0; // Default to 0 if no count found
};

const shouldExcludeFromMatches = (role, threshold = 35) => {
  return getApplicationCount(role) > threshold;
};

/**
 * Filter roles into personalized matches with progressive criteria adjustment (async due to embeddings)
 */
const getPersonalizedMatches = async (roles, candidateData, targetMin = 3, targetMax = 20) => {
  if (!candidateData || roles.length === 0) return [];
  
  console.log(`🎯 Starting personalized matching for ${roles.length} roles`);
  
  // Simple filtering based on application count only
  const eligibleForMatches = roles.filter(role => {
    const appCount = getApplicationCount(role);
    return appCount <= 35; // Standard threshold
  });
  
  console.log(`📊 Filtered to ${eligibleForMatches.length} eligible roles (application count ≤ 35)`);
  
  // Define progressive criteria levels (simplified - no experience)
  const criteriaLevels = [
    // Level 0: Strict criteria
    { salaryBuffer: 5, relaxedWorkingStyle: false, threshold: 70 },
    // Level 1: Standard criteria
    { salaryBuffer: 5, relaxedWorkingStyle: false, threshold: 50 },
    // Level 2: Relaxed salary buffer
    { salaryBuffer: 10, relaxedWorkingStyle: false, threshold: 50 },
    // Level 3: Relaxed working style
    { salaryBuffer: 10, relaxedWorkingStyle: true, threshold: 50 },
    // Level 4: Very relaxed criteria
    { salaryBuffer: 15, relaxedWorkingStyle: true, threshold: 30 },
    // Level 5: Maximum relaxation (fallback)
    { salaryBuffer: 20, relaxedWorkingStyle: true, threshold: 0 }
  ];
  
  let bestMatches = [];
  let usedLevel = 0;
  
  // Try each criteria level until we find an appropriate number of matches
  for (let i = 0; i < criteriaLevels.length; i++) {
    const level = criteriaLevels[i];
    
    console.log(`🔍 Trying level ${i}: ${level.salaryBuffer}k buffer, ${level.relaxedWorkingStyle ? 'relaxed' : 'strict'} working style, ${level.threshold}% threshold`);
    
    // Calculate match scores for all eligible roles
    const matchPromises = eligibleForMatches.map(async role => {
      const matchScore = await calculateMatchScore(role, candidateData, {
        salaryBuffer: level.salaryBuffer,
        relaxedWorkingStyle: level.relaxedWorkingStyle
      });
      
      // Calculate individual match criteria for debugging
      const hasSalaryData = candidateData.minimum_salary && candidateData.ideal_salary;
      const hasWorkingStyleData = candidateData.working_style && candidateData.working_style.length > 0;
      
      const salaryMatch = hasSalaryData ? checkSalaryMatch(role["Compensation"], candidateData.minimum_salary, candidateData.ideal_salary, level.salaryBuffer) : false;
      const workingStyleMatch = hasWorkingStyleData ? checkWorkingStyleMatch(role["Working style category"], candidateData.working_style, level.relaxedWorkingStyle) : false;
      
      // Check experience match
      const candidateExperiences = [
        candidateData.work_experience_1_role,
        candidateData.work_experience_2_role
      ].filter(Boolean);
      
      const experienceMatch = candidateExperiences.length > 0 ? 
        checkExperienceMatch(role["Role Title"], candidateExperiences) : false;
      
      return {
        ...role,
        matchScore,
        _salaryMatch: salaryMatch,
        _workingStyleMatch: workingStyleMatch,
        _experienceMatch: experienceMatch,
        _perfectMatch: salaryMatch && workingStyleMatch
      };
    });
    
    const rolesWithScores = await Promise.all(matchPromises);
    
    // Filter by threshold and sort by score
    let matches = rolesWithScores
      .filter(role => role.matchScore >= level.threshold)
      .sort((a, b) => b.matchScore - a.matchScore);
    
    // Split matches into perfect matches (both criteria) and partial matches
    const perfectMatches = matches.filter(role => role._perfectMatch);
    const partialMatches = matches.filter(role => !role._perfectMatch);
    
    // If we have 3+ perfect matches, only show those
    if (perfectMatches.length >= 3) {
      matches = perfectMatches;
      console.log(`🎯 Found ${perfectMatches.length} perfect matches - showing only perfect matches`);
    } else {
      // Otherwise, include partial matches to meet target minimum
      matches = [...perfectMatches, ...partialMatches];
      console.log(`🎯 Found ${perfectMatches.length} perfect matches + ${partialMatches.length} partial matches`);
    }
    
    // Sort by experience match first, then by application count (least applications first)
    matches.sort((a, b) => {
      // First priority: experience match
      if (a._experienceMatch && !b._experienceMatch) return -1;
      if (!a._experienceMatch && b._experienceMatch) return 1;
      
      // Second priority: application count (least applications first)
      const appCountA = getApplicationCount(a) || 0;
      const appCountB = getApplicationCount(b) || 0;
      return appCountA - appCountB;
    });
    
    // Apply final cap
    if (matches.length > targetMax) {
      matches = matches.slice(0, targetMax);
    }
    
    if (matches.length >= targetMin) {
      bestMatches = matches;
      usedLevel = i;
      console.log(`✅ Found ${matches.length} matches at level ${i}`);
      break;
    }
  }
  
  // If we still don't have enough matches, try with no threshold
  if (bestMatches.length < targetMin) {
    console.log(`⚠️ Only found ${bestMatches.length} matches, trying with no threshold...`);
    
    const allMatches = await Promise.all(eligibleForMatches.map(async role => ({
      ...role,
      matchScore: await calculateMatchScore(role, candidateData, {
        salaryBuffer: 20,
        relaxedWorkingStyle: true
      })
    })));
    
    bestMatches = allMatches
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, targetMax);
    
    console.log(`✅ Found ${bestMatches.length} matches with no threshold`);
  }
  
  return bestMatches;
};

// === SEARCH & FILTER UTILITIES ===
const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const extractSearchableText = (role) => {
  const fields = [
    role["Role Title"] || "",
    role["Name (from Startup)"] || "",
    role["Startup Description (from Startup)"] || "",
    role["Specific Experience"] || "",
    role["Job Description text"] || "",
    role["Location (from Startup)"] || "",
    role["Compensation"] || "",
    Array.isArray(role["Stream"]) ? role["Stream"].join(" ") : role["Stream"] || "",
    role["Working style category"] || "",
    role["Funding Stage (from Startup)"] || "",
  ].join(" ");
  return normalizeText(fields);
};

const calculateRelevanceScore = (role, searchTerms, searchableText) => {
  if (!searchTerms.length) return 1;
  let score = 0;
  const titleText = normalizeText(role["Role Title"] || "");
  const companyText = normalizeText(role["Name (from Startup)"] || "");
  searchTerms.forEach((term) => {
    if (titleText.includes(term)) score += 10;
    if (companyText.includes(term)) score += 8;
    if (searchableText.includes(term)) score += 3;
    const wordBoundaryRegex = new RegExp(`\\b${term}`, 'g');
    const wordMatches = searchableText.match(wordBoundaryRegex);
    if (wordMatches) score += wordMatches.length * 2;
  });
  return score;
};

const fuzzyMatch = (text, term, threshold = 0.8) => {
  if (!text || !term) return false;
  const words = text.split(' ');
  return words.some((word) => {
    if (word.length < 3) return word === term;
    let matches = 0;
    const minLength = Math.min(word.length, term.length);
    for (let i = 0; i < minLength; i++) {
      if (word[i] === term[i]) matches++;
    }
    return matches / Math.max(word.length, term.length) >= threshold;
  });
};

const filterRolesSearch = (
  roles,
  { searchQuery = '', workingStyle = [], savedOnly = false, shortlistedRoles = [] }
) => {
  return roles.filter((role) => {
    if (workingStyle.length > 0) {
      const roleWorkingStyle = role["Working style category"];
      if (!roleWorkingStyle || !workingStyle.includes(roleWorkingStyle)) return false;
    }
    if (savedOnly && !shortlistedRoles.includes(role.id)) return false;
    if (searchQuery.length > 0) {
      const searchableText = extractSearchableText(role);
      const allTermsMatch = searchQuery.split(' ').every((term) => {
        if (searchableText.includes(term)) return true;
        return fuzzyMatch(searchableText, term);
      });
      if (!allTermsMatch) return false;
    }
    return true;
  });
};

const sortRolesByRelevance = (roles, searchQuery) => {
  if (!searchQuery.trim()) return [...roles];
  const searchTerms = normalizeText(searchQuery)
    .split(' ')
    .filter(Boolean);
  return [...roles]
    .map((role) => ({
      ...role,
      _relevance: calculateRelevanceScore(role, searchTerms, extractSearchableText(role)),
    }))
    .sort((a, b) => b._relevance - a._relevance)
    .map(({ _relevance, ...rest }) => rest);
};

function HowToApplyModal({ isOpen, onClose, candidateStream, navigate }) {
  if (!isOpen) return null;

  const streamLower = (candidateStream || "").toLowerCase();
  const isSWE =
    streamLower.includes("swe") || streamLower.includes("software engineering");
  const isSales = streamLower.includes("sales");
  const isMarketingCandidate = streamLower.includes("marketing");
  const isGeneralistCandidate = streamLower.includes("generalist");

  const getInstructions = () => {
    if (isGeneralistCandidate && !isMarketingCandidate) {
      return (
        <div className="space-y-3 sm:space-y-4">
          <p>
            👁️ Before you start: Watch this{" "}
            <a
              href="https://www.loom.com/share/aa408436d9574fc291d3301b2d4960e7?sid=dec38c05-34d4-47ce-8bb3-5abc1039af4b"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              quick video
            </a>{" "}
            from our Ops Director, Bethan!
          </p>
          <p>
            🚀 Don't just window shop - apply! Spaces in the Jumpstart careers
            accelerator are highly competitive. Aim to apply for 7+ roles a
            week to maximise your chances of securing your dream role.
          </p>
          <p>
            🎥 Video pitches: Jumpstarters change their minds about a company 50%
            of the time after watching (NB some roles have 2x videos), don't miss
            out!
          </p>
    
          <p>⏰ Deadline: applications close at midnight this Sunday.</p>
          <p>
            ❌Skipping this drop? If you decide not to apply in this role drop
            (e.g. you're at interview capacity or nothing's the right fit),
            please notify{" "}
            <a
              href="mailto:info@jumpstart-uk.com"
              className="text-blue-500 underline"
            >
              info@jumpstart-uk.com
            </a>
            . If you skip two drops in a row, we may give your space to another
            candidate.
          </p>
          <p>
            For all other information, please visit our{" "}
            <button
              onClick={() => {
                onClose();
                navigate('/resources-hub');
              }}
              className="text-blue-500 underline hover:text-blue-600 transition-colors"
            >
              Resources Hub
            </button>
            . Good luck!
          </p>
        </div>
      );
    }

    if (isSWE) {
      return (
        <div className="space-y-3 sm:space-y-4">
          <p>
            🚀 Don't just window shop - apply! Spaces in the Jumpstart careers
            accelerator are highly competitive. Aim to apply for 7+ roles a
            week to maximise your chances of securing your dream role.
          </p>
          <p>
            🎥 Video pitches: Jumpstarters change their minds about a company 50%
            of the time after watching (NB some roles have 2x videos), don't miss
            out!
          </p>

          <p>⏰ Deadline: applications close at midnight this Sunday.</p>
          <p>
            ❌Skipping this drop? If you decide not to apply in this role drop
            (e.g. you're at interview capacity or nothing's the right fit),
            please notify{" "}
            <a
              href="mailto:info@jumpstart-uk.com"
              className="text-blue-500 underline"
            >
              info@jumpstart-uk.com
            </a>
            . If you skip two drops in a row, we may give your space to another
            candidate.
          </p>
          <p>
            For all other information, please visit our{" "}
            <button
              onClick={() => {
                onClose();
                navigate('/resources-hub');
              }}
              className="text-blue-500 underline hover:text-blue-600 transition-colors"
            >
              Resources Hub
            </button>
            . Good luck!
          </p>
        </div>
      );
    }

    if (isSales) {
      return (
        <div className="space-y-3 sm:space-y-4">
          <p>
            👁️ Before you start: Watch this{" "}
            <a
              href="https://www.loom.com/share/aa408436d9574fc291d3301b2d4960e7?sid=dec38c05-34d4-47ce-8bb3-5abc1039af4b"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              quick video
            </a>{" "}
            from our Ops Director, Bethan!
          </p>
          <p>
            🚀 Don't just window shop - apply! Spaces in the Jumpstart careers
            accelerator are highly competitive. Aim to apply for 7+ roles a
            week to maximise your chances of securing your dream role.
          </p>
          <p>
            🎥 Video pitches: Jumpstarters change their minds about a company 50%
            of the time after watching (NB some roles have 2x videos), don't miss
            out!
          </p>
    
          <p>⏰ Deadline: applications close at midnight this Sunday.</p>
          <p>
            ❌Skipping this drop? If you decide not to apply in this role drop
            (e.g. you're at interview capacity or nothing's the right fit),
            please notify{" "}
            <a
              href="mailto:info@jumpstart-uk.com"
              className="text-blue-500 underline"
            >
              info@jumpstart-uk.com
            </a>
            . If you skip two drops in a row, we may give your space to another
            candidate.
          </p>
          <p>
            For all other information, please visit our{" "}
            <button
              onClick={() => {
                onClose();
                navigate('/resources-hub');
              }}
              className="text-blue-500 underline hover:text-blue-600 transition-colors"
            >
              Resources Hub
            </button>
            . Good luck!
          </p>
        </div>
      );
    }

    if (isMarketingCandidate) {
      return (
        <div className="space-y-3 sm:space-y-4">
          <p>
            👁️ Before you start: Watch this{" "}
            <a
              href="https://www.loom.com/share/aa408436d9574fc291d3301b2d4960e7?sid=dec38c05-34d4-47ce-8bb3-5abc1039af4b"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              quick video
            </a>{" "}
            from our Ops Director, Bethan!
          </p>
          <p>
            🚀 Don't just window shop - apply! Spaces in the Jumpstart careers
            accelerator are highly competitive. Aim to apply for 7+ roles a
            week to maximise your chances of securing your dream role.
          </p>
          <p>
            🎥 Video pitches: Jumpstarters change their minds about a company 50%
            of the time after watching (NB some roles have 2x videos), don't miss
            out!
          </p>

          <p>⏰ Deadline: applications close at midnight this Sunday.</p>
          <p>
            ❌Skipping this drop? If you decide not to apply in this role drop
            (e.g. you're at interview capacity or nothing's the right fit),
            please notify{" "}
            <a
              href="mailto:info@jumpstart-uk.com"
              className="text-blue-500 underline"
            >
              info@jumpstart-uk.com
            </a>
            . If you skip two drops in a row, we may give your space to another
            candidate.
          </p>
          <p>
            For all other information, please visit our{" "}
            <button
              onClick={() => {
                onClose();
                navigate('/resources-hub');
              }}
              className="text-blue-500 underline hover:text-blue-600 transition-colors"
            >
              Resources Hub
            </button>
            . Good luck!
          </p>
        </div>
      );
    }

    // Fallback copy
    return (
      <div className="space-y-3 sm:space-y-4">
        <p>🤓 Click 'Learn more' to explore roles & companies.</p>
        <p>
          🎥 Some roles include a video pitch. We recommend watching! It can
          really change your perspective on a company.
        </p>
        <p>🤔 Got questions? Email Kabir or check your Notion resources.</p>
        <p>
          ✅ Keen to apply? Submit your choices & 100 word rationale by Sunday
          midnight.
        </p>
        <p>
          📚 This page will expire Tuesday. Check your email for details.
        </p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        className="bg-white w-full max-w-xl p-4 sm:p-6 rounded-lg shadow-lg relative"
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 text-gray-400 hover:text-gray-600 p-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 8.586l4.95-4.95 1.414 1.414L11.414 10l4.95 4.95-1.414 1.414L10 11.414l-4.95 4.95-1.414-1.414L8.586 10l-4.95-4.95 1.414-1.414L10 8.586z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 pr-8">
          How to Apply
        </h2>
        <div className="overflow-y-auto max-h-[60vh] sm:max-h-[70vh] text-gray-700 text-xs sm:text-sm leading-relaxed pr-1">
          {getInstructions()}
        </div>
        <div className="mt-4 sm:mt-6 text-right">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#02B491] text-white rounded-lg hover:bg-[#029078] transition-colors text-sm font-medium"
          >
            Got it
          </button>
        </div>
      </motion.div>
    </div>
  );
}

const FilterButton = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border-2 ${
      active
        ? 'bg-[#02B491] border-[#02B491] text-white shadow-sm'
        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
    }`}
  >
    {label}
  </button>
);

const workingStyleOptions = ['Hybrid', 'In person', 'Remote'];

// === MAIN COMPONENT ===

function PersonalizedRoles() {
  const query = new URLSearchParams(useLocation().search);
  const navigate = useNavigate();
  const location = useLocation();

  // Debug flag: add ?debug=1 to URL to enable debug info on role cards
  const debugMode = query.get('debug') === '1';

  // Test function for embeddings (only in debug mode)
  // Embeddings functionality removed - no longer needed



  const { pendingCount, loading: matchRequestsLoading, refreshMatchRequests } = useMatchRequests();

  // Candidate data
  const [candidateData, setCandidateData] = useState(null);
  const [candidateStatus, setCandidateStatus] = useState(null);
  const [hasBookedFirstCall, setHasBookedFirstCall] = useState(false);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  // Animate progress bar while loading
  useEffect(() => {
    let interval;
    if (loading) {
      // Reset progress to 0 when loading starts
      setProgress(0);
      interval = setInterval(() => {
        setProgress((prev) => {
          // Increment by 5, but cap at 90% until loading is complete
          return prev < 90 ? prev + 5 : prev;
        });
      }, 200);
    } else {
      // When loading stops, ensure progress is at 100%
      setProgress(100);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);

  // Roles data
  const [allRoles, setAllRoles] = useState([]);
  const [allRolesLoaded, setAllRolesLoaded] = useState(false);
  
  // View states
  const [showOtherRoles, setShowOtherRoles] = useState(false);
  const [manuallyOpenedOtherRoles, setManuallyOpenedOtherRoles] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

  // Search & filters for "all roles" view
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    workingStyle: [],
    savedOnly: false,
  });

  // Pagination for all roles
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // Save notification
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [saveNotificationMessage, setSaveNotificationMessage] = useState("");

  // Shortlisted and applied roles
  const [shortlistedRoles, setShortlistedRoles] = useState([]);
  const [shortlistedRolesLoaded, setShortlistedRolesLoaded] = useState(false);
  const [appliedRoles, setAppliedRoles] = useState([]);
  const [appliedRolesLoaded, setAppliedRolesLoaded] = useState(false);
  const [interviewingRoles, setInterviewingRoles] = useState([]);
  const [interviewingRolesLoaded, setInterviewingRolesLoaded] = useState(false);
  const [unsuccessfulRoles, setUnsuccessfulRoles] = useState([]);
  const [unsuccessfulRolesLoaded, setUnsuccessfulRolesLoaded] = useState(false);
  const [applicationPendingRoles, setApplicationPendingRoles] = useState([]);
  const [applicationPendingRolesLoaded, setApplicationPendingRolesLoaded] = useState(false);

  // Modals
  const [showMatchRequestModal, setShowMatchRequestModal] = useState(false);
  const [matchRequestNotification, setMatchRequestNotification] = useState({ show: false, message: '', type: '' });
  const [showStartupVisibilityModal, setShowStartupVisibilityModal] = useState(false);
  const [isFirstTimeLogin, setIsFirstTimeLogin] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [userName, setUserName] = useState("");


  const [showHowToApply, setShowHowToApply] = useState(false);

  // Apply notification
  const [applyNotification, setApplyNotification] = useState({ show: false, message: '', type: '' });

  // Date calculations
  const [nextDeadlineDate, setNextDeadlineDate] = useState("");

  // 1) Ensure user is logged in
  const { isAuthenticated, isChecking } = useAuthRedirect();

  // 2) Calculate deadline date
  useEffect(() => {
    const calculateNextDeadline = () => {
      const currentDate = new Date();
      const nextSunday = new Date(currentDate);
      const daysUntilSunday = (7 - currentDate.getDay()) % 7;
      nextSunday.setDate(currentDate.getDate() + daysUntilSunday);
      nextSunday.setHours(23, 59, 59, 999);

      const day = nextSunday.getDate();
      let suffix = "th";
      if (day === 1 || day === 21 || day === 31) suffix = "st";
      if (day === 2 || day === 22) suffix = "nd";
      if (day === 3 || day === 23) suffix = "rd";

      const monthsShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = monthsShort[nextSunday.getMonth()];
      const dayOfWeek = "Sunday";
      
      setNextDeadlineDate(`${dayOfWeek} ${day}${suffix} ${month}`);
    };
    calculateNextDeadline();
  }, []);

  // 3) Fetch candidate data
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    
    const fetchCandidateData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }
        
        const response = await axios.get("/api/candidates/fetchCandidate", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const candidateFields = response.data.candidate.fields;

        // Set candidate data for personalization
        let streamValue = candidateFields["Do not edit - new Stream (for visibility column)"] || "";
        if (Array.isArray(streamValue)) {
          streamValue = streamValue.length > 0 ? streamValue[0] : "";
        }
        
        const candidateInfo = {
          stream: streamValue,
          minimum_salary: candidateFields["Minimum salary"] || "",
          ideal_salary: candidateFields["Ideal salary"] || "",
          working_style: candidateFields["Preferred working style copy"] || [],
          work_experience_1_role: candidateFields["(1) Work Experience Role"] || "",
          work_experience_2_role: candidateFields["(2) Work Experience Role"] || "",
          industries: candidateFields["Industries most interested in copy"] || [],
          hasBookedFirstCall: candidateFields["booked first call (platform test)"] || false,
        };
        setCandidateData(candidateInfo);
        setHasBookedFirstCall(candidateInfo.hasBookedFirstCall);
        console.log('personalized_roles - candidateInfo.stream:', candidateInfo.stream);

        // Set other states
        const shortlisted = candidateFields["Shortlisted Roles"];
        setShortlistedRoles(Array.isArray(shortlisted) ? shortlisted : []);
        setShortlistedRolesLoaded(true);

        const statusValue = candidateFields["Jumpstart Candidate Status (from CRM)"];
        let statusString = "Unknown";
        if (Array.isArray(statusValue) && statusValue.length > 0) {
          statusString = statusValue[0];
        } else if (typeof statusValue === 'string' && statusValue.trim() !== '') {
          statusString = statusValue;
        }
        setCandidateStatus(statusString);

        // Applied roles
        const appliedToThisWeek = candidateFields["Applied this week drop"];
        const appliedRoleIds = Array.isArray(appliedToThisWeek) ? appliedToThisWeek : [];
        setAppliedRoles(appliedRoleIds);
        setAppliedRolesLoaded(true);

        // User name and first time login
        const isFirstTimeFromStorage = localStorage.getItem("isFirstTimeLogin") === "true";
        setIsFirstTimeLogin(isFirstTimeFromStorage);
        const userFullName = candidateFields["Name"] || "";
        setUserName(userFullName.split(" ")[0]);

      } catch (error) {
        console.error("Error fetching candidate data:", error);
        setShortlistedRolesLoaded(true);
        setAppliedRolesLoaded(true);
        setCandidateStatus("Error");
      }
    };
    fetchCandidateData();
  }, [isAuthenticated, isChecking]);

  // 4) Fetch all roles
  useEffect(() => {
    if (!candidateData?.stream) return;
    if (!isAuthenticated || isChecking) return;
    
    const fetchAllRoles = async () => {
      try {
        setLoading(true);
        setProgress(0);

        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }

        const response = await axios.get("/api/roles/fetchActiveRoles", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            streams: candidateData.stream,
            fetchAll: "true",
            includeSpotlights: "false",
          },
        });

        const roles = response.data.roles || [];
        setAllRoles(roles);
        setAllRolesLoaded(true);
        // Let the progress animation complete before setting loading to false
        setTimeout(() => {
          setProgress(100);
          setTimeout(() => setLoading(false), 300);
        }, 500);

        // Fetch spotlights in background
        const spotlightIdsToFetch = roles.flatMap(role => role["Spotlight"] || []);
        if (spotlightIdsToFetch.length > 0) {
          const spotlightResponse = await axios.post(
            "/api/roles/fetchSpotlightDetails",
            { spotlightIds: spotlightIdsToFetch },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const spotlightMap = spotlightResponse.data.spotlightMap || {};
          setAllRoles(prevRoles =>
            prevRoles.map(role => {
              const sIds = role["Spotlight"] || [];
              const spotlightDetails = sIds.map(id => spotlightMap[id]).filter(Boolean);
              return { ...role, spotlightDetails };
            })
          );
        }
      } catch (err) {
        console.error("Error fetching roles:", err);
        setAllRolesLoaded(true);
        // Let the progress animation complete before setting loading to false
        setTimeout(() => {
          setProgress(100);
          setTimeout(() => setLoading(false), 300);
        }, 500);
      }
    };

    fetchAllRoles();
  }, [candidateData?.stream, isAuthenticated, isChecking]);

  // 5) Fetch interview stages
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    
    const fetchInterviewingRoles = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get("/api/interviews/fetchInterviews", {
          headers: { Authorization: `Bearer ${token}` }
        });

        const interviewData = response.data.interviews || [];
        
        const interviewingRoleIds = interviewData
          .filter(interview => interview.fields["Stage"] === "Interviewing")
          .map(interview => Array.isArray(interview.fields["Role"]) ? interview.fields["Role"][0] : interview.fields["Role"])
          .filter(id => id != null);

        const applicationPendingRoleIds = interviewData
          .filter(interview => interview.fields["Stage"] === "Application pending")
          .map(interview => Array.isArray(interview.fields["Role"]) ? interview.fields["Role"][0] : interview.fields["Role"])
          .filter(id => id != null);

        const unsuccessfulStages = [
          "Rejected @ CV", "Rejected @ Interview", "Offer rejected", "Offer retracted",
          "Startup withdrawn", "Candidate withdrawn", "Rejected Interview Request"
        ];
        
        const unsuccessfulRoleIds = interviewData
          .filter(interview => unsuccessfulStages.includes(interview.fields["Stage"]))
          .map(interview => Array.isArray(interview.fields["Role"]) ? interview.fields["Role"][0] : interview.fields["Role"])
          .filter(id => id != null);
        
        setInterviewingRoles(interviewingRoleIds);
        setInterviewingRolesLoaded(true);
        setApplicationPendingRoles(applicationPendingRoleIds);
        setApplicationPendingRolesLoaded(true);
        setUnsuccessfulRoles(unsuccessfulRoleIds);
        setUnsuccessfulRolesLoaded(true);
      } catch (error) {
        console.error("Error fetching interview stages:", error);
        setInterviewingRolesLoaded(true);
        setUnsuccessfulRolesLoaded(true);
      }
    };

    fetchInterviewingRoles();
  }, [isAuthenticated, isChecking]);

  // Get personalized matches (using useState and useEffect due to async nature)
  const [personalizedMatches, setPersonalizedMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesLoaded, setMatchesLoaded] = useState(false);
  // Embeddings functionality removed - no longer needed

  useEffect(() => {
    const loadPersonalizedMatches = async () => {
      if (!candidateData || !allRolesLoaded || allRoles.length === 0) {
        setPersonalizedMatches([]);
        setMatchesLoaded(true);
        return;
      }
      
      setMatchesLoading(true);
      setMatchesLoaded(false);
      console.log('🚀 Starting personalized matching (no experience criteria)...');
      
      try {
        const matches = await getPersonalizedMatches(allRoles, candidateData);
        
        // Add debug info to each role if in debug mode
        if (debugMode) {
          const matchesWithDebug = matches.map(role => {
            // Calculate individual match criteria for debugging
            const hasSalaryData = candidateData.minimum_salary && candidateData.ideal_salary;
            const hasWorkingStyleData = candidateData.working_style && candidateData.working_style.length > 0;
            
            const salaryMatch = hasSalaryData ? checkSalaryMatch(role["Compensation"], candidateData.minimum_salary, candidateData.ideal_salary, 5) : false;
            const workingStyleMatch = hasWorkingStyleData ? checkWorkingStyleMatch(role["Working style category"], candidateData.working_style, false) : false;
            
            // Enhanced experience matching debug
            const candidateExperiences = [
              candidateData.work_experience_1_role,
              candidateData.work_experience_2_role
            ].filter(Boolean);
            
            const experienceMatch = candidateExperiences.length > 0 ? 
              checkExperienceMatch(role["Role Title"], candidateExperiences) : false;
            
            const experienceDebug = debugExperienceMatch(role["Role Title"], candidateExperiences);
            
            return {
              ...role,
              _debugInfo: {
                candidateData: candidateData,
                salaryMatch: salaryMatch,
                workingStyleMatch: workingStyleMatch,
                experienceMatch: experienceMatch,
                experienceDebug: experienceDebug,
                embeddingsUsed: false, // No longer used
                similarityScore: 0, // No longer used
                similarityThreshold: 0, // No longer used
                baseMatchScore: role.matchScore,
                similarityBonus: 0, // No longer used
                // Add salary parsing debug info
                roleSalaryRange: parseSalaryRange(role["Compensation"]),
                candidateSalaryRange: {
                  min: parseCandidateSalary(candidateData.minimum_salary),
                  max: parseCandidateSalary(candidateData.ideal_salary)
                },
                salaryOverlapDetails: `Role(${parseSalaryRange(role["Compensation"])?.min || 'N/A'}-${parseSalaryRange(role["Compensation"])?.max || 'N/A'}) vs Candidate(${parseCandidateSalary(candidateData.minimum_salary) || 'N/A'}-${parseCandidateSalary(candidateData.ideal_salary) || 'N/A'} ±5k)`
              }
            };
          });
          setPersonalizedMatches(matchesWithDebug);
        } else {
          setPersonalizedMatches(matches);
        }
        
        console.log('✅ Personalized matching completed successfully');
      } catch (error) {
        console.error('❌ Error in personalized matching:', error);
        setPersonalizedMatches([]);
      } finally {
        setMatchesLoading(false);
        setMatchesLoaded(true);
      }
    };

    loadPersonalizedMatches();
  }, [allRoles, allRolesLoaded, candidateData, debugMode]);

  // Get remaining roles (all except matches) - using useState and useEffect due to async nature
  const [remainingRoles, setRemainingRoles] = useState([]);
  const [remainingRolesLoaded, setRemainingRolesLoaded] = useState(false);

  useEffect(() => {
    const loadRemainingRoles = async () => {
      if (!allRolesLoaded || allRoles.length === 0 || !candidateData) {
        setRemainingRoles(allRoles);
        setRemainingRolesLoaded(true);
        return;
      }
      
      // Wait for personalized matches to be loaded
      if (!matchesLoaded) {
        return;
      }
      
    const matchIds = new Set(personalizedMatches.map(role => role.id));
      const remaining = allRoles.filter(role => !matchIds.has(role.id));
      
      // Add debug info to remaining roles if in debug mode
      if (debugMode) {
        console.log('🔄 Adding debug info to remaining roles...');
        const remainingWithDebug = remaining.map(role => {
          // Enhanced experience matching debug
          const candidateExperiences = [
            candidateData.work_experience_1_role,
            candidateData.work_experience_2_role
          ].filter(Boolean);
          
          const experienceMatch = candidateExperiences.length > 0 ? 
            checkExperienceMatch(role["Role Title"], candidateExperiences) : false;
          
          const experienceDebug = debugExperienceMatch(role["Role Title"], candidateExperiences);
          
          return {
            ...role,
            _debugInfo: {
              candidateData: candidateData,
              criteriaLevel: 'N/A (Other Roles)',
              salaryMatch: checkSalaryMatch(role["Compensation"], candidateData.minimum_salary, candidateData.ideal_salary, 5),
              workingStyleMatch: checkWorkingStyleMatch(role["Working style category"], candidateData.working_style, false),
              experienceMatch: experienceMatch,
              experienceDebug: experienceDebug,
              embeddingsUsed: false, // No longer used
              similarityScore: 0, // No longer used
              similarityThreshold: 0, // No longer used
              baseMatchScore: 0, // Not calculated for remaining roles
              similarityBonus: 0, // No longer used
              // Add salary parsing debug info
              roleSalaryRange: parseSalaryRange(role["Compensation"]),
              candidateSalaryRange: {
                min: parseCandidateSalary(candidateData.minimum_salary),
                max: parseCandidateSalary(candidateData.ideal_salary)
              },
              salaryOverlapDetails: `Role(${parseSalaryRange(role["Compensation"])?.min || 'N/A'}-${parseSalaryRange(role["Compensation"])?.max || 'N/A'}) vs Candidate(${parseCandidateSalary(candidateData.minimum_salary) || 'N/A'}-${parseCandidateSalary(candidateData.ideal_salary) || 'N/A'} ±5k)`
            }
          };
        });
        setRemainingRoles(remainingWithDebug);
        console.log('✅ Debug info added to remaining roles');
      } else {
        setRemainingRoles(remaining);
      }
      
      setRemainingRolesLoaded(true);
    };

    loadRemainingRoles();
  }, [allRoles, allRolesLoaded, personalizedMatches, candidateData, debugMode, matchesLoaded]);

  const [tempSearchQuery, setTempSearchQuery] = useState("");

  // Debounce temp search -> searchQuery
  useEffect(() => {
    const t = setTimeout(() => {
      if (tempSearchQuery !== searchQuery) {
        setSearchQuery(tempSearchQuery);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [tempSearchQuery, searchQuery]);

  // Automatically toggle "Other Roles" section based on search or filter presence
  useEffect(() => {
    const hasSearch = searchQuery.trim().length > 0;
    const hasFilters = activeFilters.savedOnly || activeFilters.workingStyle.length > 0;

    if ((hasSearch || hasFilters) && !showOtherRoles) {
      setShowOtherRoles(true);
    }

    // Only auto-close if user didn't manually open the section and there are no search/filters
    if (!hasSearch && !hasFilters && showOtherRoles && !manuallyOpenedOtherRoles) {
      setShowOtherRoles(false);
    }
  }, [searchQuery, activeFilters, showOtherRoles, manuallyOpenedOtherRoles]);

  // Add helper to retrieve application count for a role (used to sort matches by popularity)
  const getApplicationCount = (role) => {
    const possibleFields = [
      "# Apps this month",
      "Apps this month",
      "Applications this month",
      "# Applications this month",
      "App Count",
      "Application Count"
    ];
    for (const fieldName of possibleFields) {
      const appCount = role[fieldName];
      if (appCount !== undefined && appCount !== null) {
        const count = parseInt(appCount);
        if (!isNaN(count)) {
          return count;
        }
      }
    }
    // If no count is available, push to the end by returning a large number
    return Number.POSITIVE_INFINITY;
  };

  // Unified search across all roles
  const allFilteredRoles = useMemo(() => {
    if (!allRolesLoaded || !matchesLoaded || !remainingRolesLoaded) return [];
    
    // Combine all roles for unified search
    const allRoles = [...personalizedMatches, ...remainingRoles];
    
    // Apply search and filters to all roles
    const filtered = sortRolesByRelevance(
              filterRolesSearch(allRoles, {
          searchQuery,
          workingStyle: activeFilters.workingStyle,
          savedOnly: activeFilters.savedOnly,
          shortlistedRoles,
        }),
      searchQuery
    );

    // Sort by application count (less applied-to roles first)
    return filtered.sort((a, b) => getApplicationCount(a) - getApplicationCount(b));
  }, [allRolesLoaded, matchesLoaded, remainingRolesLoaded, personalizedMatches, remainingRoles, searchQuery, activeFilters, shortlistedRoles]);

  // Separate filtered roles for display purposes
  const filteredPersonalizedMatches = useMemo(() => {
    if (!matchesLoaded) return [];
    
    // If there's a search query or filters, use the unified search results
    if (searchQuery.trim() || activeFilters.savedOnly || activeFilters.workingStyle.length > 0) {
      const matchIds = new Set(personalizedMatches.map(role => role.id));
      return allFilteredRoles.filter(role => matchIds.has(role.id));
    }
    
    // Otherwise, use the original personalized matches
    return personalizedMatches.sort((a, b) => getApplicationCount(a) - getApplicationCount(b));
  }, [matchesLoaded, personalizedMatches, allFilteredRoles, searchQuery, activeFilters]);

  const filteredRoles = useMemo(() => {
    if (!remainingRolesLoaded) return [];
    
    // If there's a search query or filters, use the unified search results
    if (searchQuery.trim() || activeFilters.savedOnly || activeFilters.workingStyle.length > 0) {
      const remainingIds = new Set(remainingRoles.map(role => role.id));
      return allFilteredRoles.filter(role => remainingIds.has(role.id));
    }
    
    // Otherwise, use the original remaining roles
    return sortRolesByRelevance(remainingRoles, searchQuery);
  }, [remainingRolesLoaded, remainingRoles, allFilteredRoles, searchQuery, activeFilters]);

  // Check if we should show unified results (when there are no personalized matches or when filtering)
  const shouldShowUnifiedResults = useMemo(() => {
    const hasSearchOrFilters = searchQuery.trim() || activeFilters.savedOnly || activeFilters.workingStyle.length > 0;
    const noPersonalizedMatches = filteredPersonalizedMatches.length === 0;
    
    // Show unified results when there are no personalized matches OR when filtering
    return hasSearchOrFilters && noPersonalizedMatches;
  }, [searchQuery, activeFilters, filteredPersonalizedMatches.length]);

  // Modal effects
  useEffect(() => {
    if (isFirstTimeLogin && candidateStatus === "Live") {
      setShowWelcomeModal(true);
    }
  }, [isFirstTimeLogin, candidateStatus]);

  useEffect(() => {
    if (isFirstTimeLogin && candidateStatus === "Live" && !showWelcomeModal) {
      setShowStartupVisibilityModal(true);
    }
  }, [isFirstTimeLogin, candidateStatus, showWelcomeModal]);

  // Event handlers
  const handleSaveToggle = async (isSaved, roleTitle, roleId) => {
    setSaveNotificationMessage(
      isSaved ? `${roleTitle} added to shortlist` : `${roleTitle} removed from shortlist`
    );
    setShowSaveNotification(true);
    setTimeout(() => setShowSaveNotification(false), 2000);

    setShortlistedRoles((prev) => {
      if (isSaved) {
        return [...prev, roleId];
      } else {
        return prev.filter((id) => id !== roleId);
      }
    });
  };

  const handleApplyNow = async (roleId, roleTitle, rationale = null) => {
    if (appliedRoles.includes(roleId) || applicationPendingRoles.includes(roleId)) {
      setApplyNotification({
        show: true,
        message: `You've already applied to ${roleTitle}!`,
        type: 'info'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
      return false;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const data = {
        fields: {
          Role: [roleId],
          "Match type": "Application",
          Stage: "Application pending",
          ...(rationale && { "Application Rationale": rationale }),
        },
        token: token,
      };

      await axios.post("/api/interviews/createInterview", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setAppliedRoles(prev => [...prev, roleId]);
      setApplyNotification({
        show: true,
        message: `Successfully applied to ${roleTitle}!`, 
        type: 'success'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
      return true;

    } catch (err) {
      console.error("Error applying to role:", err);
      let errorMessage = `Error applying to ${roleTitle}.`;
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      setApplyNotification({
        show: true,
        message: errorMessage,
        type: 'error'
      });
      setTimeout(() => setApplyNotification({ show: false, message: '', type: '' }), 3000);
      return false;
    }
  };

  const handleMatchRequestActionComplete = (action, roleTitle) => {
    setMatchRequestNotification({
      show: true,
      message: action === 'accepted' 
        ? `✅ Great! You've accepted the interview request for ${roleTitle}. Check your email for next steps.`
        : `❌ You've declined the interview request for ${roleTitle}.`,
      type: action === 'accepted' ? 'success' : 'info'
    });
    setTimeout(() => setMatchRequestNotification({ show: false, message: '', type: '' }), 4000);
    refreshMatchRequests();
  };

  const handleBookCall = () => {
    setHasBookedFirstCall(true);
    setMatchRequestNotification({
      show: true,
      message: '✅ Prep call booking status updated! Check your email for the session details.',
      type: 'success'
    });
    setTimeout(() => setMatchRequestNotification({ show: false, message: '', type: '' }), 4000);
  };

  const handleStartupVisibilityComplete = async () => {
    setShowStartupVisibilityModal(false);
    setIsFirstTimeLogin(false);
    localStorage.removeItem("isFirstTimeLogin");
  };

  // Check if candidate is not Live
  if (candidateStatus !== null && candidateStatus !== "Live") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4 sm:p-8">
        <div className="w-full max-w-3xl text-center">
          <h2 className="text-2xl font-semibold mb-4">No Roles Available</h2>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading && !allRolesLoaded) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4 sm:p-8">
        <div className="w-full max-w-3xl">
          <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-6">
            <motion.div
              className="absolute left-0 top-0 h-full bg-[#02B491]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <p className="text-center text-gray-600 font-medium mb-8">
            Loading your roles...
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm p-4 animate-pulse"
              >
                <div className="h-32 sm:h-48 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while matches are being processed
  if (matchesLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4 sm:p-8">
        <div className="w-full max-w-3xl">
          <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-6">
            <motion.div
              className="absolute left-0 top-0 h-full bg-[#02B491]"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 2, ease: "easeInOut" }}
            />
          </div>
          <p className="text-center text-gray-600 font-medium mb-8">
            Processing role matches with AI...
          </p>
          <p className="text-center text-gray-500 text-sm mb-8">
            This may take a few moments
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm p-4 animate-pulse"
              >
                <div className="h-32 sm:h-48 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // --- Weekly application progress ---
  const applicationTarget = 7;
  const appliedCount = appliedRoles.filter(roleId => !unsuccessfulRoles.includes(roleId)).length;

  return (
    <div className="flex flex-col items-start bg-white min-h-screen pl-0 md:pl-20 pt-4 md:pt-2">
      <SaveNotification show={showSaveNotification} message={saveNotificationMessage} />
      <SaveNotification show={applyNotification.show} message={applyNotification.message} type={applyNotification.type} />
      <SaveNotification show={matchRequestNotification.show} message={matchRequestNotification.message} type={matchRequestNotification.type} />
      
      {showMatchRequestModal && (
        <MatchRequestNotification
          onClose={() => setShowMatchRequestModal(false)}
          onActionComplete={handleMatchRequestActionComplete}
          candidateStream={candidateData?.stream}
          hasBookedFirstCall={hasBookedFirstCall}
          onBookCall={handleBookCall}
        />
      )}
      
      {showWelcomeModal && (
        <WelcomeModal
          isOpen={showWelcomeModal}
          onClose={() => setShowWelcomeModal(false)}
          userName={userName}
        />
      )}

      <AnimatePresence>
        {showHowToApply && (
          <HowToApplyModal
            isOpen={showHowToApply}
            onClose={() => setShowHowToApply(false)}
            candidateStream={candidateData?.stream}
            navigate={navigate}
          />
        )}
      </AnimatePresence>

      {showStartupVisibilityModal && (
        <StartupVisibilityModal
          isOpen={showStartupVisibilityModal}
          onClose={() => setShowStartupVisibilityModal(false)}
          onComplete={handleStartupVisibilityComplete}
        />
      )}

      <div className="w-full p-4 sm:p-6 md:px-12 md:py-8 flex flex-col h-full max-w-[1800px] mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 md:mb-12 px-4 sm:px-0"
        >
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 sm:mb-6">
            {userName ? `Welcome back,  ${userName}!` : ''}
          </h1>

          <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
            {/* NEW: Integrated Interview Request Section */}
            {!matchRequestsLoading && pendingCount > 0 && (
              <div className="bg-red-50 border-b border-red-200 p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 flex-grow min-w-0">
                    <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C13.1 2 14 2.9 14 4V5.08C16.84 5.56 19 8.03 19 11V16L21 18V19H3V18L5 16V11C5 8.03 7.16 5.56 10 5.08V4C10 2.9 10.9 2 12 2ZM12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22Z"/>
                      </svg>
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="text-sm font-semibold text-red-700 truncate">
                        {pendingCount} interview request{pendingCount !== 1 ? 's' : ''} waiting!
                      </p>
                      <p className="text-xs text-red-600 hidden sm:block truncate">
                        Companies want to interview you - respond quickly.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowMatchRequestModal(true)}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-red-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    Review Now
                  </button>
                </div>
              </div>
            )}
            {/* Existing content of the banner */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 sm:p-6 gap-6 md:gap-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-[#02B491]/10 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-[#02B491]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    Application Deadline
                  </h3>
                  <p className="text-sm text-gray-800">
                    <span className="font-bold text-[#02B491]">
                      {nextDeadlineDate}
                    </span>{" "}
                    at midnight
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-[#02B491]/10 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-[#02B491]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="min-w-[160px]">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-gray-900">
                    Apply to 7+ roles per week
                    </h3>

                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-[#02B491] h-full rounded-full transition-all duration-300"
                        style={{ width: `${appliedRolesLoaded && unsuccessfulRolesLoaded ? Math.min((appliedCount / applicationTarget) * 100, 100) : 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                      {appliedRolesLoaded && unsuccessfulRolesLoaded ? `${appliedCount}/${applicationTarget}` : `0/${applicationTarget}`}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/resources')}
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-[#02B491] text-white rounded-lg hover:bg-[#029078] transition-colors text-sm font-medium whitespace-nowrap shadow-sm hover:shadow md:ml-4"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                View Resources
              </button>
            </div>
          </div>
        </motion.div>
        
          {/* Unified Roles Section */}
        <div className="px-4 sm:px-0">
          {/* Header with Search */}
          <div className="flex justify-between items-center mb-6">
            {!showOtherRoles && (
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 flex items-baseline gap-2">
                  {`${filteredPersonalizedMatches.length} matches! ✨ \n\n`}
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  Curated using your salary, working-style &amp; work experience 
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
          
                  {personalizedMatches._criteriaLevel > 1 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Expanded criteria
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Search Input */}
          <div className={`w-full transition-all duration-300 mb-6`}>
            <div className="relative w-full mb-3">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              </div>
              <input type="text" className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#02B491]/20 focus:border-[#02B491] transition-all text-gray-900 placeholder-gray-500 shadow-sm" placeholder="Search roles by title, company, keyword..." value={tempSearchQuery} onChange={(e)=>setTempSearchQuery(e.target.value)} />
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                <span className="font-medium">Filters:</span>
              </div>
              <button
                onClick={() => setActiveFilters(prev => ({...prev, savedOnly: !prev.savedOnly}))}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeFilters.savedOnly
                    ? 'bg-[#02B491] text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                My saved roles
              </button>
              {/* Role Title Filters */}
              {candidateData?.work_experience_1_role && (
                <button
                  onClick={() => {
                    const roleTitle = candidateData.work_experience_1_role;
                    const isActive = searchQuery.includes(roleTitle);
                    
                    if (isActive) {
                      // Remove from search query
                      const newQuery = searchQuery.replace(new RegExp(`\\b${roleTitle}\\b`, 'gi'), '').trim();
                      setSearchQuery(newQuery);
                      setTempSearchQuery(newQuery);
                    } else {
                      // Add to search query
                      const newQuery = searchQuery ? `${searchQuery} ${roleTitle}` : roleTitle;
                      setSearchQuery(newQuery);
                      setTempSearchQuery(newQuery);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    searchQuery.includes(candidateData.work_experience_1_role)
                      ? 'bg-[#02B491] text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {candidateData.work_experience_1_role}
                </button>
              )}
              
              {candidateData?.work_experience_2_role && (
                <button
                  onClick={() => {
                    const roleTitle = candidateData.work_experience_2_role;
                    const isActive = searchQuery.includes(roleTitle);
                    
                    if (isActive) {
                      // Remove from search query
                      const newQuery = searchQuery.replace(new RegExp(`\\b${roleTitle}\\b`, 'gi'), '').trim();
                      setSearchQuery(newQuery);
                      setTempSearchQuery(newQuery);
                    } else {
                      // Add to search query
                      const newQuery = searchQuery ? `${searchQuery} ${roleTitle}` : roleTitle;
                      setSearchQuery(newQuery);
                      setTempSearchQuery(newQuery);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    searchQuery.includes(candidateData.work_experience_2_role)
                      ? 'bg-[#02B491] text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {candidateData.work_experience_2_role}
                </button>
              )}
              
              {/* Industries Filters */}
              {candidateData?.industries && candidateData.industries.length > 0 && candidateData.industries.map((industry, index) => (
                <button
                  key={index}
                  onClick={() => {
                    const isActive = searchQuery.includes(industry);
                    
                    if (isActive) {
                      // Remove from search query
                      const newQuery = searchQuery.replace(new RegExp(`\\b${industry}\\b`, 'gi'), '').trim();
                      setSearchQuery(newQuery);
                      setTempSearchQuery(newQuery);
                    } else {
                      // Add to search query
                      const newQuery = searchQuery ? `${searchQuery} ${industry}` : industry;
                      setSearchQuery(newQuery);
                      setTempSearchQuery(newQuery);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    searchQuery.includes(industry)
                      ? 'bg-[#02B491] text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {industry}
                </button>
              ))}
                              {(searchQuery || activeFilters.savedOnly) && (
                  <button 
                    onClick={() => {
                      setSearchQuery("");
                      setTempSearchQuery("");
                      setActiveFilters({
                        workingStyle: [],
                        savedOnly: false,
                      });
                      setShowOtherRoles(false);
                      setManuallyOpenedOtherRoles(false);
                    }}
                    className="text-sm text-gray-500 hover:text-gray-800 font-medium"
                  >
                    Clear all
                  </button>
                )}
            </div>
          </div>

          {/* Your Matches Section */}
          {filteredPersonalizedMatches.length > 0 ? (
            <>
              <div className="mb-6">
  
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10 mb-8">
                {filteredPersonalizedMatches.map((role, index) => (
                  <motion.div
                    key={role.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    className="relative overflow-visible"
                  >
                    <RoleCard
                      role={role}
                      currentlyPlaying={currentlyPlaying}
                      setCurrentlyPlaying={setCurrentlyPlaying}
                      viewMode="grid"
                      onSaveToggle={(isSaved, roleTitle) => handleSaveToggle(isSaved, roleTitle, role.id)}
                      shortlistedRoles={shortlistedRoles}
                      shortlistedRolesLoaded={shortlistedRolesLoaded}
                      onApplyNow={handleApplyNow}
                      appliedRoles={appliedRoles}
                      appliedRolesLoaded={appliedRolesLoaded}
                      interviewingRoles={interviewingRoles}
                      interviewingRolesLoaded={interviewingRolesLoaded}
                      unsuccessfulRoles={unsuccessfulRoles}
                      unsuccessfulRolesLoaded={unsuccessfulRolesLoaded}
                      applicationPendingRoles={applicationPendingRoles}
                      applicationPendingRolesLoaded={applicationPendingRolesLoaded}
                      debug={debugMode}
                      candidateData={candidateData}
                      onBookCall={handleBookCall}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Show Other Roles Button */}
              {!showOtherRoles && filteredRoles.length > 0 && !searchQuery.trim() && !activeFilters.savedOnly && activeFilters.workingStyle.length === 0 && (
                <div className="text-center mb-12">
                  <div className="inline-flex flex-col items-center">
                    {/* Subtle divider line */}
                    <div className="w-24 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-6"></div>
                    
                    {/* Enhanced button */}
                  <button
                      onClick={() => {
                        setShowOtherRoles(true);
                        setManuallyOpenedOtherRoles(true);
                      }}
                      className="inline-flex items-center gap-3 px-8 py-4 bg-[#02B491] text-white rounded-xl hover:bg-[#029078] transition-colors duration-200 text-base font-semibold shadow-sm hover:shadow-md"
                    >
                      {/* Simple icon */}
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                      
                      {/* Text */}
                      <span>View Other Roles</span>
                      
                      {/* Clean count badge */}
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                        {filteredRoles.length}
                      </span>
                  </button>
                    
                    {/* Subtle description */}
                    <p className="text-sm text-gray-500 mt-3 max-w-xs text-center">
                      Explore additional opportunities that might interest you
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* No Personalized Matches - Show unified search results if there are any */
            shouldShowUnifiedResults && allFilteredRoles.length > 0 && (
              <>
                {/* Show unified search results */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                      <h3 className="text-lg font-semibold text-gray-900">Search Results</h3>
                      <span className="text-sm text-gray-500">({allFilteredRoles.length})</span>
                    </div>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    Results for your search across all available roles
                  </p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10">
                  {allFilteredRoles.map((role, index) => (
                    <motion.div
                      key={role.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                    >
                      <RoleCard
                        role={role}
                        currentlyPlaying={currentlyPlaying}
                        setCurrentlyPlaying={setCurrentlyPlaying}
                        viewMode="grid"
                        onSaveToggle={(isSaved, roleTitle) => handleSaveToggle(isSaved, roleTitle, role.id)}
                        shortlistedRoles={shortlistedRoles}
                        shortlistedRolesLoaded={shortlistedRolesLoaded}
                        onApplyNow={handleApplyNow}
                        appliedRoles={appliedRoles}
                        appliedRolesLoaded={appliedRolesLoaded}
                        interviewingRoles={interviewingRoles}
                        interviewingRolesLoaded={interviewingRolesLoaded}
                        unsuccessfulRoles={unsuccessfulRoles}
                        unsuccessfulRolesLoaded={unsuccessfulRolesLoaded}
                        applicationPendingRoles={applicationPendingRoles}
                        applicationPendingRolesLoaded={applicationPendingRolesLoaded}
                        debug={debugMode}
                        candidateData={candidateData}
                      />
                    </motion.div>
                  ))}
                </div>
              </>
            )
          )}

          {/* Other Roles Section - Show when button is clicked OR when search/filters are active, but not when showing unified results */}
          {showOtherRoles && filteredRoles.length > 0 && !shouldShowUnifiedResults && (
            <>
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {searchQuery.trim() || activeFilters.savedOnly || activeFilters.workingStyle.length > 0
                        ? "Additional Results" 
                        : "Other Roles"}
                    </h3>
                    <span className="text-sm text-gray-500">({filteredRoles.length})</span>
                  </div>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>
                                  <p className="text-sm text-gray-600 mb-6">
                    {searchQuery.trim() || activeFilters.savedOnly || activeFilters.workingStyle.length > 0
                      ? "Additional roles matching your search criteria"
                      : "Additional opportunities that may interest you"
                    }
                  {personalizedMatches._totalAvailable && personalizedMatches._eligibleForMatches && 
                   personalizedMatches._totalAvailable > personalizedMatches._eligibleForMatches && (
                    <span className="text-gray-500 ml-1">
                    </span>
                  )}
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10">
                {filteredRoles.map((role, index) => (
                  <motion.div
                    key={role.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: (filteredPersonalizedMatches.length + index) * 0.05 }}
                  >
                    <RoleCard
                      role={role}
                      currentlyPlaying={currentlyPlaying}
                      setCurrentlyPlaying={setCurrentlyPlaying}
                      viewMode="grid"
                      onSaveToggle={(isSaved, roleTitle) => handleSaveToggle(isSaved, roleTitle, role.id)}
                      shortlistedRoles={shortlistedRoles}
                      shortlistedRolesLoaded={shortlistedRolesLoaded}
                      onApplyNow={handleApplyNow}
                      appliedRoles={appliedRoles}
                      appliedRolesLoaded={appliedRolesLoaded}
                      interviewingRoles={interviewingRoles}
                      interviewingRolesLoaded={interviewingRolesLoaded}
                      unsuccessfulRoles={unsuccessfulRoles}
                      unsuccessfulRolesLoaded={unsuccessfulRolesLoaded}
                      applicationPendingRoles={applicationPendingRoles}
                      applicationPendingRolesLoaded={applicationPendingRolesLoaded}
                      debug={debugMode}
                      candidateData={candidateData}
                      onBookCall={handleBookCall}
                    />
                  </motion.div>
                ))}
              </div>
            </>
          )}

          {/* No Results State */}
          {matchesLoaded && remainingRolesLoaded && filteredPersonalizedMatches.length === 0 && filteredRoles.length === 0 && (!shouldShowUnifiedResults || allFilteredRoles.length === 0) && (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-100">
              <div className="max-w-md mx-auto">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No Roles Found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your search terms or clearing the search to see all available roles.
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setTempSearchQuery("")}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#02B491] text-white rounded-lg hover:bg-[#029078] transition-colors text-sm font-medium"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <HelpPanel candidateStream={candidateData?.stream} />
      

    </div>
  );
}

export default PersonalizedRoles; 